import React, { useState } from 'react';

import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { AnimatePresence } from 'framer-motion';

import { InfoIcon } from '@pkg/assets/floorGame';
import { Portal, PortalArea } from '@pkg/components';

import { IconWrapper, StyledInfoAction, Tooltip, TooltipInterior, TooltipMessage } from './InfoAction.style';

export interface InfoActionProps {
    // TODO: We may want to instead pass `children`.
    message: string;
    ariaLabel?: string;
}

// TODO: This needs to surface a tooltip on `:hover/:focus`.
export function InfoAction({ message, ariaLabel = 'Focus to read more' }: InfoActionProps) {
    const [open, onOpenChange] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange,
        placement: 'bottom-start',
        middleware: [offset(10), flip({ fallbackAxisSideDirection: 'end' }), shift()],
        whileElementsMounted: autoUpdate,
    });

    // TODO: Sticking with `useClick()` because the keyboard-tabbing
    // behaviour is not as expected when using `useFocus()`.
    const click = useClick(context);
    // const hover = useHover(context);
    // const focus = useFocus(context, { enabled: false });

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const dropdownMarkup =
        message && open ? (
            <Portal destination={PortalArea.Transition}>
                <FloatingFocusManager context={context} modal={false}>
                    <Tooltip ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                        <TooltipInterior>
                            <TooltipMessage>{message}</TooltipMessage>
                        </TooltipInterior>
                    </Tooltip>
                </FloatingFocusManager>
            </Portal>
        ) : null;

    return (
        <StyledInfoAction ref={refs.setReference} aria-label={ariaLabel} {...getReferenceProps()}>
            <IconWrapper>
                <InfoIcon />
            </IconWrapper>

            <AnimatePresence>{dropdownMarkup}</AnimatePresence>
        </StyledInfoAction>
    );
}
